import { useCallback, useEffect } from 'react'
import { useMedia } from 'react-use'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { DeliveryType, LineItem } from 'shared-types'
import { CartDrawerFooter } from '~/components/CartDrawerFooter'
import { CartItemCard, CartItemCardProps } from '~/components/CartItemCard'
import { CartPromoBanner } from '~/components/CartPromoBanner'
import { CartUpsellProductSlider } from '~/components/CartUpsellProductSlidier'
import { Drawer } from '~/components/Drawer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  useFormatPrice,
  usePage,
  useWishlist,
  useUserPreferredStore,
} from '~/hooks'
import { useCart, useCartDrawer } from '~/hooks/useCart'
import { GTM, GTMEvent } from '~/lib'
import { Button } from '../Button'
import EmptyContainerPlaceholder from '../EmptyContainerPlaceholder'
import styles from './CartDrawer.module.css'
import { CartDrawerProps } from './CartDrawer.types'

const TABLET_DRAWER_WIDTH = 480

export const CartDrawer = ({ payLaterProviders }: CartDrawerProps) => {
  const { store } = useUserPreferredStore()
  const router = useRouter()
  const { cart, upsellProducts, getUpsellProducts, updateCart } = useCart(false)
  const isSelectedDelivery =
    cart?.deliveryType === DeliveryType.DELIVERY ||
    cart?.deliveryType === DeliveryType.WAREHOUSE
  const { closeCartDrawer, isCartOpen } = useCartDrawer()
  const cartItemsProductsToSave = cart?.lineItems?.map((lineItem) => {
    return { sku: lineItem.variant.sku, quantity: lineItem.quantity }
  })
  const { createList } = useWishlist(cartItemsProductsToSave, false, true)
  const { formatPrice } = useFormatPrice()
  const isTabletBreakpoint = useMedia('(max-width: 1080px)', true)
  const page = usePage()
  const isRTM = page.brand === 'rtm'

  const totalItemsInCart = cart?.lineItems?.length || 0

  const isNextEligiblePromotionVisible =
    cart?.nextEligiblePromotion &&
    cart?.subTotal?.centAmount < cart?.freeShippingThreshold

  useEffect(() => {
    if (isCartOpen) {
      getUpsellProducts?.()
    }
  }, [isCartOpen, getUpsellProducts])

  const handleChangeQuantity = useCallback(
    (quantity, item) => {
      updateCart.mutate(
        [
          {
            quantity,
            sku: item.variant.sku,
            lineItemId: item.id,
          },
        ],
        {
          onSuccess(data) {
            GTM.dispatch(GTMEvent.CLEAR_ECOMMERCE)
            GTM.dispatch(GTMEvent.CART_CONTENTS_PUSH, data)
          },
        }
      )
      GTM.dispatch(GTMEvent.CLEAR_ECOMMERCE)
      GTM.dispatch(GTMEvent.REMOVE_FROM_CART, {
        removedProduct: { ...item, sku: item.variant.sku },
      })
    },
    [updateCart]
  )

  const drawerTitle = (
    <div className='flex items-baseline gap-4'>
      <Typography
        tag={TypographyTag.p}
        variant={
          isRTM ? TypographyVariant.Heading5 : TypographyVariant.BodyXLargeBold
        }>
        Your Cart
      </Typography>
      {cart?.lineItems?.length > 0 && (
        <Button
          variant='ghost'
          type='button'
          style={{
            '--btn-ghost-text-decoration': 'underline',
            '--btn-font-weight': 400,
            '--btn-line-height':
              'var(--typography-body-regular-line-height-extra)',
            '--btn-size-md-padding': 0,
            '--btn-size-md-height': 'max-content',
          }}
          data-id='addToWishlistButton'
          onClick={() => {
            return createList('1')
          }}>
          Save cart
        </Button>
      )}
    </div>
  )

  const getLineItemNotice = (item: LineItem): CartItemCardProps['notice'] => {
    if (item.isMarketplaceProduct || item.notice?.message) {
      return {
        ...item.notice,
      }
    }
  }

  const renderContent = () => {
    return (
      <div className='flex-1 w-full p-4 lg:py-6 2xl:px-6 overflow-y-auto'>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodyLarge}
          className={styles.productQuantity}>
          <span className='inline-block font-extrabold'>
            {totalItemsInCart}
          </span>{' '}
          {totalItemsInCart === 1 ? 'Product' : 'Products'}
        </Typography>
        {totalItemsInCart > 0 ? (
          <>
            {isNextEligiblePromotionVisible && (
              <div className='pt-4'>
                <CartPromoBanner>
                  <>
                    Spend another{' '}
                    <span className='font-extrabold inline-block'>
                      {formatPrice(cart.nextEligiblePromotion.difference)}
                    </span>{' '}
                    for{' '}
                    <span className='font-extrabold inline-block'>
                      {cart.nextEligiblePromotion.title}
                    </span>
                  </>
                </CartPromoBanner>
              </div>
            )}
            <div className='flex flex-col gap-y-4 pt-4 lg:pt-6'>
              {cart?.lineItems?.map((item, index) => {
                return (
                  <div
                    key={`${item.id}_${index}`}
                    className='pb-4 border-b border-primary-border'>
                    <CartItemCard
                      isSelectedDelivery={isSelectedDelivery}
                      storeName={store?.displayName}
                      item={item}
                      isEditable
                      isLoading={false}
                      notice={getLineItemNotice(item)}
                      onChangeQuantity={(quantity) => {
                        handleChangeQuantity(quantity, item)
                      }}
                    />
                  </div>
                )
              })}
            </div>
            {upsellProducts && (
              <div className='mt-10'>
                <CartUpsellProductSlider {...upsellProducts} />
              </div>
            )}
          </>
        ) : (
          <div className='pt-4'>
            <EmptyContainerPlaceholder
              title='Your bag is empty'
              description='Add products to your cart to continue.'
              image={{
                src:
                  page.brand === 'rtm'
                    ? '/images/empty-bag-rtm.svg'
                    : '/images/empty-bag-jaycar.svg',
              }}
            />
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', closeCartDrawer)
    return () => {
      router.events.off('routeChangeComplete', closeCartDrawer)
    }
  }, [router, closeCartDrawer])

  return (
    <>
      <Drawer
        wrapperClass={classNames(styles.wrapper, 'hidden md:block')}
        title={drawerTitle}
        isOpen={isCartOpen}
        placement='right'
        width={isTabletBreakpoint ? TABLET_DRAWER_WIDTH : undefined}
        onClose={closeCartDrawer}>
        {renderContent()}
        <div
          className={classNames(
            styles.footerBottom,
            'px-4 py-6 2xl:px-6 border-t border-primary-border'
          )}>
          <CartDrawerFooter
            payLaterProviders={payLaterProviders}
            points={cart?.points || 0}
            hasItemsInCart={totalItemsInCart !== 0}
            subtotal={cart?.subTotal}
            onClose={closeCartDrawer}
            promotionSavings={cart?.promotionSavings}
          />
        </div>
      </Drawer>
      <Drawer
        wrapperClass={classNames(styles.wrapperBottom, 'md:hidden')}
        title={drawerTitle}
        isOpen={isCartOpen}
        placement='bottom'
        onClose={closeCartDrawer}>
        {renderContent()}
        <div
          className={classNames(
            styles.footerBottom,
            'py-6 px-4 border-t border-primary-border'
          )}>
          <CartDrawerFooter
            payLaterProviders={payLaterProviders}
            points={cart?.points || 0}
            hasItemsInCart={totalItemsInCart !== 0}
            subtotal={cart?.subTotal}
            onClose={closeCartDrawer}
            promotionSavings={cart?.promotionSavings}
          />
        </div>
      </Drawer>
    </>
  )
}
